<template>
  <div class="flex flex-col gap-6">
    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center p-12 gap-2 text-gray-500">
      <theme-icon icon="loading" type="icons" classes="w-8 h-8 animate-spin" />
      <p>Loading permissions...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center gap-3 p-4 bg-red-50 border-l-4 border-red-500 rounded text-red-600">
      <theme-icon icon="alert-circle" type="icons" classes="w-5 h-5" />
      <p>{{ error }}</p>
    </div>

    <template v-else>
      <!-- Info Banner for Brokers -->
      <div v-if="isBrokerUser" class="flex items-start gap-3 p-4 mb-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded text-blue-700 dark:text-blue-300">
        <theme-icon icon="info" type="icons" classes="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium mb-1">Save Client Permissions First</p>
          <p class="text-sm">Select at least one client permission below and click "Save" to unlock access to Project and Site permissions.</p>
        </div>
      </div>
      
      <!-- Global Permissions Section -->
      <themed-card v-if="globalPermissionTypes.length > 0" class="mb-6 pt-6">
        <div class="mb-4">
          <h3 class="text-lg font-medium m-0 mb-1">Global Permissions</h3>
          <p class="text-sm text-gray-600 m-0">These permissions apply across all clients</p>
        </div>
        <div class="flex flex-wrap gap-4">
          <themed-checkbox
            v-for="permission in globalPermissionTypes"
            :key="permission.id"
            :id="`global-${permission.name}`"
            :label="permission.display_name || permission.name"
            :checked="hasGlobalPermission(permission.name)"
            @change="updateGlobalPermission(permission.name, $event)"
          />
        </div>
      </themed-card>

      <!-- Entity-Level Permissions Table -->
      <themed-card class="overflow-hidden pt-6">
        <div class="mb-4">
          <h3 class="text-lg font-medium m-0 mb-1">Client-Specific Permissions</h3>
          <p class="text-sm text-gray-600 m-0">These permissions apply to specific clients</p>
        </div>

        <div class="flex gap-3 items-center justify-end mb-4 flex-wrap">
          <themed-search
            v-model="searchQuery"
            placeholder="Search clients..."
            class="min-w-[250px] max-w-[250px]"
          />
        </div>
        
        <themed-table
          :columns="tableColumns"
          :data="filteredClients"
          :loading="loading"
          empty-message="No clients found"
          :pagination="true"
          :per-page="perPage"
        >
          <template #column-name="{ row }">
            <div class="font-medium">{{ row.name }}</div>
          </template>

          <template v-for="permission in entityLevelPermissionTypes" v-slot:[`column-${permission.name}`]="{ row }">
            <div :key="permission.id" class="flex justify-center items-center">
              <themed-checkbox
                :checked="hasPermission(row.id, permission.name)"
                @change="updatePermission(row, permission.name, $event)"
              />
            </div>
          </template>
        </themed-table>
      </themed-card>
    </template>
  </div>
</template>

<script>
import ThemedCard from '../../ThemedCard.vue';
import ThemedSearch from '../../ThemedSearch.vue';
import ThemedCheckbox from '../../ThemedCheckbox.vue';
import ThemedTable from '../../ThemedTable.vue';
import ThemeIcon from '../../ThemeIcon.vue';

export default {
  name: 'ClientPermissions',

  components: {
    ThemedCard,
    ThemedSearch,
    ThemedCheckbox,
    ThemedTable,
    ThemeIcon
  },

  props: {
    userId: {
      type: [Number, String],
      required: true
    },
    clientId: {
      type: [Number, String],
      required: false,
      default: null
    }
  },

  data() {
    return {
      clients: [],
      permissionTypes: [],
      loading: false,
      saving: false,
      error: null,
      searchQuery: '',
      permissionGroup: 'client',
      userPermissions: {},
      globalPermissions: [],
      originalPermissions: {}, // Track original state for change detection
      hasChanges: false,
      perPage: 10
    };
  },

  computed: {
    filteredClients() {
      if (!this.searchQuery) {
        return this.clients;
      }
      const query = this.searchQuery.toLowerCase();
      return this.clients.filter(client =>
        client.name.toLowerCase().includes(query) ||
        (client.address || '').toLowerCase().includes(query)
      );
    },

    globalPermissionTypes() {
      return this.permissionTypes.filter(pt => pt.is_global === true);
    },

    entityLevelPermissionTypes() {
      return this.permissionTypes.filter(pt => pt.is_global === false);
    },
    
    isBrokerUser() {
      // Check if we're managing a broker user
      const user = this.$store?.state?.users?.selectedUser;
      if (!user || !user.role) return false;
      return user.role.name === 'broker' || user.role_id === 4;
    },

    tableColumns() {
      const columns = [
        { field: 'name', label: 'Client', sortable: true }
      ];

      this.entityLevelPermissionTypes.forEach(permission => {
        columns.push({
          field: permission.name,
          label: permission.display_name || permission.name,
          sortable: false,
          align: 'center'
        });
      });

      return columns;
    }
  },

  watch: {
    userId: {
      immediate: false,
      handler(newUserId, oldUserId) {
        if (newUserId && newUserId !== oldUserId && newUserId !== 'new') {
          this.fetchData();
        }
      }
    },
    clientId: {
      immediate: false,
      handler(newClientId, oldClientId) {
        if (newClientId !== oldClientId) {
          this.fetchData();
        }
      }
    }
  },

  created() {
    this.fetchData();
    
    // Listen for save events from parent
    this.$eventHub.$on('save-user-client-permissions', this.saveAllPermissions);
    this.$eventHub.$on('save-all-permissions', this.saveAllPermissions);
  },

  beforeDestroy() {
    this.$eventHub.$off('save-user-client-permissions', this.saveAllPermissions);
    this.$eventHub.$off('save-all-permissions', this.saveAllPermissions);
  },

  methods: {
    hasPermission(clientId, permissionName) {
      return this.userPermissions[clientId] && Array.isArray(this.userPermissions[clientId])
        ? this.userPermissions[clientId].includes(permissionName)
        : false;
    },

    hasGlobalPermission(permissionName) {
      return this.globalPermissions.includes(permissionName);
    },

    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        // Only fetch permissions - the API returns clients with permission states
        const permissionsUrl = this.userId 
          ? `/api/users/${this.userId}/permissions/${this.permissionGroup}`
          : null;

        if (!permissionsUrl) {
          this.loading = false;
          return;
        }

        const permissionsRes = await axios.get(permissionsUrl);

        const responseData = permissionsRes.data?.data || {};
        const items = responseData.items || [];
        const groupData = responseData.group || {};

        this.permissionTypes = groupData.permission_types || [];

        // Extract clients from items (excluding global permissions item)
        this.clients = items.filter(item => item.id !== null);

        this.userPermissions = {};
        this.globalPermissions = [];

        if (Array.isArray(items)) {
          items.forEach(item => {
            const clientPermissions = [];

            if (item.permissions && typeof item.permissions === 'object') {
              this.permissionTypes.forEach(pt => {
                const permName = typeof pt === 'object' ? pt.name : pt;
                if (item.permissions[permName] === true) {
                  if (item.id === null) {
                    this.globalPermissions.push(permName);
                  } else {
                    clientPermissions.push(permName);
                  }
                }
              });
            }

            if (item.id !== null) {
              this.$set(this.userPermissions, item.id, clientPermissions);
            }
          });
        }

        const typesRes = await axios.get('/api/permission-types', {
          params: { group: this.permissionGroup }
        });

        this.permissionTypes = Array.isArray(typesRes.data)
          ? typesRes.data
          : (typesRes.data.data || []);
        
        // Save original state for change tracking
        this.saveOriginalState();
        
        // Don't emit here - only emit after save
        // The tabs should only appear after permissions are actually saved

      } catch (error) {
        console.error('Failed to fetch data:', error);
        this.error = 'Failed to load permissions. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    updatePermission(client, permissionName, isChecked) {
      if (!this.userPermissions[client.id]) {
        this.$set(this.userPermissions, client.id, []);
      }

      if (isChecked) {
        if (!this.userPermissions[client.id].includes(permissionName)) {
          this.userPermissions[client.id].push(permissionName);
        }
      } else {
        const index = this.userPermissions[client.id].indexOf(permissionName);
        if (index > -1) {
          this.userPermissions[client.id].splice(index, 1);
        }
      }
      
      this.checkForChanges();
    },

    updateGlobalPermission(permissionName, isChecked) {
      if (isChecked) {
        if (!this.globalPermissions.includes(permissionName)) {
          this.globalPermissions.push(permissionName);
        }
      } else {
        const index = this.globalPermissions.indexOf(permissionName);
        if (index > -1) {
          this.globalPermissions.splice(index, 1);
        }
      }
      
      console.log('Updated globalPermissions:', JSON.parse(JSON.stringify(this.globalPermissions)));
      this.checkForChanges();
    },
    
    checkForChanges() {
      const currentState = JSON.stringify({
        userPermissions: this.userPermissions,
        globalPermissions: [...this.globalPermissions].sort()
      });
      const originalState = JSON.stringify(this.originalPermissions);
      this.hasChanges = currentState !== originalState;
    },
    
    saveOriginalState() {
      this.originalPermissions = {
        userPermissions: JSON.parse(JSON.stringify(this.userPermissions)),
        globalPermissions: [...this.globalPermissions].sort()
      };
      this.hasChanges = false;
    },

    async saveAllPermissions() {
      this.saving = true;
      this.error = null;

      const promises = [];

      try {
        // Compare current state with original state
        const originalUserPerms = this.originalPermissions.userPermissions || {};
        const originalGlobalPerms = this.originalPermissions.globalPermissions || [];

        // Check for changes in entity-level permissions
        const allClientIds = new Set([
          ...Object.keys(originalUserPerms),
          ...Object.keys(this.userPermissions)
        ]);

        allClientIds.forEach(clientId => {
          const originalPerms = [...(originalUserPerms[clientId] || [])].sort();
          const currentPerms = [...(this.userPermissions[clientId] || [])].sort();

          if (JSON.stringify(originalPerms) !== JSON.stringify(currentPerms)) {
            console.log(`ClientPermissions: Client ${clientId} permissions changed`);
            
            const permissionsObj = {};
            this.permissionTypes.forEach(pt => {
              const permName = typeof pt === 'object' ? pt.name : pt;
              permissionsObj[permName] = currentPerms.includes(permName);
            });

            promises.push(
              axios.post('/api/users/' + this.userId + '/permissions', {
                permissionable_type: this.permissionGroup,
                permissionable_id: parseInt(clientId),
                permissions: permissionsObj
              })
            );
          }
        });

        // Check for changes in global permissions
        const currentGlobalSorted = [...this.globalPermissions].sort();
        const originalGlobalSorted = [...originalGlobalPerms].sort();
        
        if (JSON.stringify(currentGlobalSorted) !== JSON.stringify(originalGlobalSorted)) {
          const globalPermissionsObj = {};
          this.permissionTypes.forEach(pt => {
            const permName = typeof pt === 'object' ? pt.name : pt;
            globalPermissionsObj[permName] = this.globalPermissions.includes(permName);
          });

          promises.push(
            axios.post('/api/users/' + this.userId + '/permissions', {
              permissionable_type: this.permissionGroup,
              permissionable_id: null,
              permissions: globalPermissionsObj
            })
          );
        }

        if (promises.length === 0) {
          this.saving = false;
          this.$eventHub.$emit('permission-saved', { section: 'Client Permissions', hasChanges: false });
          return;
        }

        await Promise.all(promises);
        
        // Update original state after successful save
        this.saveOriginalState();
        
        // Check if broker has any client permissions now
        const hasClientPermissions = Object.keys(this.userPermissions).length > 0 || this.globalPermissions.length > 0;
        this.$eventHub.$emit('client-permissions-updated', hasClientPermissions);
        
        // Trigger reload of project/site permissions since client access changed
        this.$eventHub.$emit('reload-project-site-permissions');
        
        this.$eventHub.$emit('permission-saved', { section: 'Client Permissions', hasChanges: true });

      } catch (error) {
        console.error('Error saving client permissions:', error);
        this.error = 'Failed to save permissions. Please try again.';
        
        this.$eventHub.$emit('permission-save-error');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
